import React, { useEffect, useState } from "react";
import {
  DesplegableModel,
  Evolucion,
  EvolucionError,
  Paciente,
  Usuario,
} from "../../interfaces";
import {
  IonButton,
  IonCol,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonText,
  IonModal,
  IonRow,
  IonTextarea,
  IonTitle,
  IonToolbar,
  IonCheckbox,
  IonNote,
} from "@ionic/react";
import StyledButton from "../StyledButton/StyledButton";
import dayjs from "dayjs";
import "dayjs/locale/es";
import CustomDesplegable from "../CustomDesplegable/CustomDesplegable";
import { search, searchOutline } from "ionicons/icons";
import useNotificacion from "../../hooks/notificacion";
import RtfEditor from "../RtfEditor/RtfEditor";
import { useSession } from "../../hooks/SessionContext";

import http from "../../hooks/httpAdapter";
import { useHistory } from "react-router";

interface ContainerProps {
  evolucionSel?: Evolucion;
  evolucionAnt?: Evolucion;
  abrirModal: boolean;
  setAbrirModal: (abrir: boolean) => void;
  setCerradaPorConfirmar: (seAgrego: boolean) => void;
}
const ModalEditorEvoluciones: React.FC<ContainerProps> = ({
  evolucionSel,
  evolucionAnt,
  abrirModal,
  setAbrirModal,
  setCerradaPorConfirmar,
}) => {
  const DIAGNOSTICO_POR_DEFECTO = "AACONTROL";
  const [diagnosticosSelect, setDiagnosticosSelect] = useState<
    DesplegableModel[]
  >([]);
  const [evolucion, setEvolucion] = useState<Evolucion>({
    codigo: 0,
    fecha: dayjs().format("YYYY-MM-DD"),
    prestadorCod: 0,
    prestadorNom: "",
    pacienteCod: 0,
    motivoConsulta: "Consulta médica",
    motivo: "",
    diagnosticoCod: 0,
    diagnosticoNom: "",
    derivanteMat: "",
    derivanteNom: "",
  });
  const [errorresEvolucion, setErrorresEvolucion] = useState<EvolucionError>({
    motivoConsulta: false,
    motivoConsultaMessage: "",
    motivo: false,
    motivoMessage: "",
    diagnosticoCod: false,
    diagnosticoCodMessage: "",
  });
  const [openDiagnosticosSelect, setOpenDiagnosticosSelect] = useState(false);
  const [usarEvolucionAnterior, setUsarEvolucionAnterior] = useState(false);

  const {
    hcUL,
    cargandoSession,
    pacienteCargado,
    setSesionInvalida,
    mostrarNotificacion,
  } = useSession();
  const history = useHistory();

  const TraerDiagnosticos = async () => {
    const config = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
    };

    try {
      const response = await http()
        .get(`Diagnosticos`, config)
        .catch((e) => {
          if (e.response && e.response.status === 401) {
            setSesionInvalida(true);
            // history.push("/", { motivoRedireccion: "401" }); // Redirección en caso de error 401
          } else {
            console.error(e);
          }
        });

      let listado: DesplegableModel[] = [];
      for (let i = 0; i < response.length; i++) {
        listado.push({
          codigo: response[i].codigo,
          text: response[i].nombre,
        });
      }
      setDiagnosticosSelect(listado);
    } catch (error) {
      //@ts-ignore
      mostrarNotificacion(true, "Error: " + error.response, "rojo");
      return [];
    }
  };

  const limpiarEvolucion = () => {
    setEvolucion({
      codigo: -1,
      fecha: dayjs().format("YYYY-MM-DD"),
      prestadorCod: hcUL?.prestadorCod || 0,
      prestadorNom: hcUL?.prestadorNom || "",
      pacienteCod: pacienteCargado?.codigo || 0,
      motivoConsulta: "Consulta médica",
      motivo: "",
      diagnosticoCod: 0,
      diagnosticoNom: "",
      derivanteMat: "",
      derivanteNom: "",
    });
  };

  const EsEditable = (fecha: string) => {
    let fechaEvolucion = dayjs(fecha);
    let fechaLimite = dayjs().subtract(1, "day"); // Restar 1 día a la fecha actual

    return (
      evolucionSel?.prestadorCod === hcUL?.prestadorCod &&
      fechaEvolucion.isAfter(fechaLimite)
    );
  };

  const aplicarDiagnosticoPorDefecto = (diagnosticos: DesplegableModel[]) => {
    const diagnosticoDefault = diagnosticos.find(
      (diagnostico) =>
        diagnostico.text.trim().toUpperCase() === DIAGNOSTICO_POR_DEFECTO,
    );

    if (!diagnosticoDefault) return;

    setEvolucion((evolucionActual) => {
      if (evolucionActual.diagnosticoCod > 0) {
        return evolucionActual;
      }

      return {
        ...evolucionActual,
        diagnosticoCod: diagnosticoDefault.codigo,
        diagnosticoNom: diagnosticoDefault.text,
      };
    });
  };

  useEffect(() => {
    if (cargandoSession === false) {
      if (pacienteCargado !== null && pacienteCargado.codigo !== 0) {
        if (abrirModal) {
          TraerDiagnosticos();
          if (
            evolucionSel &&
            evolucionSel !== undefined &&
            pacienteCargado !== null
          ) {
            setEvolucion({
              ...evolucionSel,
              pacienteCod: pacienteCargado?.codigo || 0,
            });
          } else {
            setUsarEvolucionAnterior(false);
            limpiarEvolucion();
          }
        }
      } else {
        mostrarNotificacion(
          true,
          "Error: " + "No se encuentra al paciente",
          "rojo",
        );
      }
    }
  }, [evolucionSel, evolucionAnt, abrirModal, cargandoSession]);

  useEffect(() => {
    if (abrirModal && !evolucionSel) {
      if (usarEvolucionAnterior) {
        if (
          evolucionAnt &&
          evolucionAnt !== undefined &&
          pacienteCargado !== null
        ) {
          setEvolucion({
            ...evolucionAnt,
            pacienteCod: pacienteCargado?.codigo || 0,
          });
        }
      } else {
        limpiarEvolucion();
      }
    }
  }, [
    usarEvolucionAnterior,
    abrirModal,
    evolucionSel,
    evolucionAnt,
    pacienteCargado,
  ]);

  useEffect(() => {
    if (
      abrirModal &&
      !evolucionSel &&
      !usarEvolucionAnterior &&
      diagnosticosSelect.length > 0
    ) {
      aplicarDiagnosticoPorDefecto(diagnosticosSelect);
    }
  }, [abrirModal, evolucionSel, usarEvolucionAnterior, diagnosticosSelect]);

  const handleChangeInput = (value: any, id: string) => {
    let evolucionModificada = { ...evolucion };

    switch (id) {
      case "Diagnóstico":
        diagnosticosSelect.forEach((diagnostico) => {
          if (diagnostico.codigo === value) {
            evolucionModificada.diagnosticoCod = diagnostico.codigo;
            evolucionModificada.diagnosticoNom = diagnostico.text;
          }
        });
        break;
      case "Motivo":
        evolucionModificada.motivoConsulta = value;
        break;
      case "evolucion":
        evolucionModificada.motivo = value;
        break;
      default:
        mostrarNotificacion(true, "Cambio inválida", "rojo");
        break;
    }
    setEvolucion(evolucionModificada);
  };

  const validarEvolucion = () => {
    let { motivoConsulta, diagnosticoCod, motivo } = evolucion;
    const errores = errorresEvolucion;
    let mensajeError = "";
    if (motivoConsulta.trim() === "") {
      mensajeError = "El motivo de consulta no puede estar vacio";
      errores.motivoConsulta = true;
      errores.motivoConsultaMessage = mensajeError;
      mostrarNotificacion(true, mensajeError, "rojo");
    } else {
      errores.motivoConsulta = false;
      errores.motivoConsultaMessage = "";
    }
    if (diagnosticoCod > 0) {
      errores.diagnosticoCod = false;
      errores.diagnosticoCodMessage = "";
    } else {
      mensajeError = "El diagnóstico no puede estar vacio";
      errores.diagnosticoCod = true;
      errores.diagnosticoCodMessage = mensajeError;
      mostrarNotificacion(true, mensajeError, "rojo");
    }

    let evolucionRTF = motivo;
    let i = evolucionRTF.indexOf("<body>");

    if (i !== -1) {
      const i2 = evolucionRTF.indexOf("</body>");
      evolucionRTF = motivo.slice(i + 6, i2);
    }

    if (evolucionRTF.trim() === "") {
      mensajeError = "La Evolución de la consulta no puede quedar vacia";
      errores.motivo = true;
      errores.motivoMessage = mensajeError;
      mostrarNotificacion(true, mensajeError, "rojo");
    } else {
      errores.motivo = false;
      errores.motivoMessage = "";
    }

    setErrorresEvolucion(errores);
    return errores.motivo || errores.motivoConsulta || errores.diagnosticoCod
      ? false
      : true;
  };

  const agregarEvolucion = async () => {
    let motivoEvolucion2 = evolucion.motivo;
    let i = motivoEvolucion2.indexOf("<body>");

    if (i !== -1) {
      const i2 = motivoEvolucion2.indexOf("</body>");
      let motivo2 = evolucion.motivo.slice(i + 6, i2);
      motivoEvolucion2 = motivo2;
    }
    motivoEvolucion2 = "<p>" + motivoEvolucion2 + "</p>";

    var config = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
    };

    try {
      const response = await http()
        .post(
          `Evoluciones/Agregar`,
          {
            codigo: evolucion.codigo,
            prestadorCod: hcUL?.prestadorCod,
            pacienteCod: pacienteCargado?.codigo,
            motivoConsulta: evolucion.motivoConsulta,
            motivo: motivoEvolucion2,
            diagnosticoCod: evolucion.diagnosticoCod,
          },

          config,
        )
        .catch((e) => {
          if (e.response && e.response.status === 401) {
            setSesionInvalida(true);
            // history.push("/", { motivoRedireccion: "401" }); // Redirección en caso de error 401
          } else {
            console.error(e);
          }
        });
      if (response) {
        mostrarNotificacion(
          true,
          "La evolución se Registro Correctamente",
          "verde",
        );
        limpiarEvolucion();
        setCerradaPorConfirmar(true);
        setAbrirModal(false);
      } else {
        mostrarNotificacion(
          true,
          "Error al intentar registrar la evolución",
          "rojo",
        );
      }
    } catch (error: any) {
      mostrarNotificacion(true, error, "verde");
    }
  };

  const editarEvolucion = async () => {
    let motivoEvolucion2 = evolucion.motivo;
    let i = motivoEvolucion2.indexOf("<body>");

    if (i !== -1) {
      const i2 = motivoEvolucion2.indexOf("</body>");
      let motivo2 = evolucion.motivo.slice(i + 6, i2);
      motivoEvolucion2 = motivo2;
    }

    motivoEvolucion2 = "<p>" + motivoEvolucion2 + "</p>";

    let Estado = 0;
    let respuesta = "";
    let errorAxios = "";
    let blnErrorAxios = false;

    var config = {
      headers: { Authorization: `Bearer ${hcUL?.token}` },
    };

    try {
      const response = await http()
        .patch(
          `Evoluciones/Modificar`,
          {
            codigo: evolucion.codigo,
            prestadorCod: hcUL?.prestadorCod,
            pacienteCod: pacienteCargado?.codigo,
            motivoConsulta: evolucion.motivoConsulta,
            motivo: motivoEvolucion2,
            diagnosticoCod: evolucion.diagnosticoCod,
          },

          config,
        )
        .catch((e) => {
          if (e.response && e.response.status === 401) {
            setSesionInvalida(true);
            // history.push("/", { motivoRedireccion: "401" }); // Redirección en caso de error 401
          } else {
            console.error(e);
          }
        });

      Estado = response.status;
      respuesta = response.statusText;
    } catch (error) {
      //@ts-ignore
      errorAxios = error.response;
      blnErrorAxios = true;
    }
    if (blnErrorAxios === false) {
      mostrarNotificacion(
        true,
        "La evolución se modifico correctamente",
        "verde",
      );
      limpiarEvolucion();
      setCerradaPorConfirmar(true);
      setAbrirModal(false);
    } else {
      mostrarNotificacion(true, errorAxios, "roj");
    }
  };

  const handleClickGrabar = async (evolucionGrabar: Evolucion) => {
    try {
      if (validarEvolucion() === false) {
        return;
      }

      if (evolucionGrabar?.codigo !== 0 && evolucionSel !== undefined) {
        if (EsEditable(evolucionGrabar?.fecha)) {
          await editarEvolucion();
        } else {
          mostrarNotificacion(
            true,
            "la evolucion solo puede ser modificada el mismo dia que fue emitida.",
            "rojo",
          );
        }
      } else {
        await agregarEvolucion();
      }
    } catch (e) {
      mostrarNotificacion(true, "Ocurrio un error al querer grabar.", "rojo");
    }
  };

  return (
    <IonModal isOpen={abrirModal} onDidDismiss={() => setAbrirModal(false)}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            <h6>
              {evolucionSel?.fecha && EsEditable(evolucionSel?.fecha) ? (
                <>
                  Editar evolución:{" "}
                  <strong>
                    {pacienteCargado?.apellido}, {pacienteCargado?.nombre}
                  </strong>
                </>
              ) : (
                <>
                  Nueva evolución:{" "}
                  <strong>
                    {pacienteCargado?.apellido}, {pacienteCargado?.nombre}
                  </strong>
                </>
              )}{" "}
            </h6>
          </IonTitle>

          <StyledButton
            slot="end"
            fill="clear"
            expand="full"
            onClick={() => setAbrirModal(false)}
          >
            Salir
          </StyledButton>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen={true}>
        <IonRow>
          {" "}
          {!evolucionSel && (
            <IonCol size="12">
              <IonItem>
                <IonCheckbox
                  checked={usarEvolucionAnterior}
                  onClick={() =>
                    setUsarEvolucionAnterior(!usarEvolucionAnterior)
                  }
                  onIonChange={(e) =>
                    handleChangeInput(e.target.value, "Motivo")
                  }
                >
                  Usar evolución anterior
                </IonCheckbox>
              </IonItem>
            </IonCol>
          )}
          <IonCol size="12">
            <IonItem
              className={`${!errorresEvolucion.motivoConsulta && "ion-valid"}`}
              lines="none"
            >
              <IonInput
                label="Motivo"
                label-placement="floating"
                id="Motivo"
                value={evolucion.motivoConsulta}
                onIonChange={(e) => handleChangeInput(e.target.value, "Motivo")}
                errorText={errorresEvolucion.motivoConsultaMessage}
              />

              {errorresEvolucion.motivoConsulta && (
                <IonNote slot="error">
                  <IonText color={"danger"}>
                    <small>{errorresEvolucion.motivoConsultaMessage}</small>
                  </IonText>
                </IonNote>
              )}
            </IonItem>
          </IonCol>
          <IonCol size="12">
            <IonItem
              className={`${errorresEvolucion.diagnosticoCod && "ion-valid"}`}
              lines="none"
            >
              <IonInput
                readonly
                label="Diagnóstico"
                label-placement="floating"
                id="Diagnóstico"
                value={evolucion.diagnosticoNom}
                onClick={() => setOpenDiagnosticosSelect(true)}
                errorText={errorresEvolucion.diagnosticoCodMessage}
              />
              <IonButton
                slot="end"
                fill="clear"
                onClick={() => setOpenDiagnosticosSelect(true)}
              >
                <IonIcon
                  aria-hidden="true"
                  ios={searchOutline}
                  md={search}
                  size="large"
                />
              </IonButton>

              {errorresEvolucion.diagnosticoCod && (
                <IonNote slot="error">
                  <IonText color={"danger"}>
                    <small>{errorresEvolucion.diagnosticoCodMessage}</small>
                  </IonText>
                </IonNote>
              )}
              <CustomDesplegable
                array={diagnosticosSelect}
                value={evolucion.diagnosticoCod}
                handleChange={handleChangeInput}
                mostrarTodos={false}
                mostrarSearch={true}
                id="Diagnóstico"
                open={openDiagnosticosSelect}
                setOpen={setOpenDiagnosticosSelect}
              />
            </IonItem>
          </IonCol>
        </IonRow>

        <IonRow className=" ion-align-content-start ion-align-items-start">
          <IonCol size="12">
            <IonItem
              lines="none"
              className={`${errorresEvolucion.motivo && "ion-invalid"}`}
            >
              <IonText>
                <h4>Evolución</h4>
                <small>
                  ATENCION: NO SE VA A TENER EN CONSIDERACION TABULACIONES NI
                  ESTILIZADOS
                </small>
              </IonText>
            </IonItem>
            <RtfEditor
              id="evolucion"
              handleChange={handleChangeInput}
              defaultValue={evolucion.motivo}
              readOnly={false}
              seeToolbar={false}
              returnHtml={false}
            />
            <IonItem
              lines="none"
              className={`${errorresEvolucion.motivo && "ion-invalid"}`}
            >
              <IonText color={"danger"}>
                <small>{errorresEvolucion.motivoMessage}</small>
              </IonText>
            </IonItem>
          </IonCol>
        </IonRow>
        <IonRow className="ion-align-items-center">
          <IonCol size="6">
            <StyledButton expand="block" onClick={() => setAbrirModal(false)}>
              Cancelar
            </StyledButton>
          </IonCol>
          {evolucionSel?.fecha && EsEditable(evolucionSel?.fecha) ? (
            <IonCol size="6">
              <StyledButton
                expand="block"
                onClick={() => handleClickGrabar(evolucion)}
              >
                Grabar Cambios
              </StyledButton>
            </IonCol>
          ) : (
            <IonCol size="6">
              <StyledButton
                expand="block"
                onClick={() => handleClickGrabar(evolucion)}
              >
                Guardar Nueva Evolución
              </StyledButton>
            </IonCol>
          )}
        </IonRow>
      </IonContent>
    </IonModal>
  );
};

export default ModalEditorEvoluciones;
